<template>
  <section class="journey" aria-label="Application process">
    <div class="journey__viewport">
      <ol class="journey__rail">
        <li
          v-for="stage in renderedStages"
          :key="stage.label"
          class="journey__stage"
          :class="{
            'journey__stage--reached': stage.reached,
            'journey__stage--current': stage.current,
            'journey__stage--connector-reached': stage.connectorReached,
          }"
          :style="{
            '--stage-color': stage.color,
            '--stage-soft-color': stage.softColor,
          }"
        >
          <span class="journey__label">{{ stage.label }}</span>
          <span class="journey__line" aria-hidden="true"></span>
          <span class="journey__marker" aria-hidden="true">
            <font-awesome-icon v-if="stage.reached" icon="check" />
          </span>
          <span class="journey__date">{{ stage.date ? formatStageDate(stage.date) : '' }}</span>
        </li>
      </ol>
    </div>

    <div v-if="currentStatus || toggleable" class="journey__footer">
      <span
        v-if="currentStatus"
        class="journey__current"
        :style="{ '--current-stage-color': currentStageColor }"
      >
        <span class="journey__current-dot" aria-hidden="true"></span>
        {{ currentStatus }}
      </span>
      <button
        v-if="toggleable"
        type="button"
        class="journey__toggle"
        :aria-expanded="expanded"
        aria-controls="application-step-panel"
        @click="$emit('toggle')"
      >
        <span>Next step</span>
        <font-awesome-icon icon="chevron-down" :class="{ 'journey__toggle-icon--open': expanded }" />
      </button>
    </div>

    <Transition name="journey-form">
      <div v-if="expanded && toggleable" class="journey__form">
        <slot name="form"></slot>
      </div>
    </Transition>
  </section>
</template>

<script lang="ts">
import { computed, type PropType } from 'vue';
import type { ProcessStep } from '../types';
import {
  APPLICATION_STAGES,
  getApplicationStageIndex,
  type ApplicationStage,
} from '../utils/applicationStages';

interface RenderedJourneyStage extends ApplicationStage {
  reached: boolean;
  current: boolean;
  connectorReached: boolean;
  date: string | null;
}

export default {
  name: 'ApplicationJourney',
  props: {
    steps: { type: Array as PropType<ProcessStep[]>, default: () => [] },
    currentStatus: { type: String, default: '' },
    foundDate: { type: String, default: '' },
    expanded: { type: Boolean, default: false },
    toggleable: { type: Boolean, default: false },
  },
  emits: ['toggle'],
  setup(props: {
    steps: ProcessStep[];
    currentStatus: string;
    foundDate: string;
    expanded: boolean;
    toggleable: boolean;
  }) {
    const currentStageIndex = computed<number>(() => (
      getApplicationStageIndex(props.currentStatus, props.steps)
    ));

    const renderedStages = computed<RenderedJourneyStage[]>(() => APPLICATION_STAGES.map((stage, index) => {
      const matchingSteps = props.steps
        .filter((step) => stage.statuses.includes(step.process_type) && step.process_date)
        .sort((a, b) => a.process_date.localeCompare(b.process_date));

      return {
        ...stage,
        reached: index <= currentStageIndex.value,
        current: index === currentStageIndex.value,
        connectorReached: index < currentStageIndex.value,
        date: matchingSteps[0]?.process_date || (index === 0 ? props.foundDate : null),
      };
    }));

    const currentStageColor = computed<string>(() => (
      APPLICATION_STAGES[currentStageIndex.value]?.color || '#0a8bc2'
    ));

    const formatStageDate = (dateString: string): string => {
      const [year, month, day] = dateString.slice(0, 10).split('-').map(Number);
      if (!year || !month || !day) return dateString;
      return new Intl.DateTimeFormat('en', { month: 'short', day: 'numeric' }).format(
        new Date(year, month - 1, day)
      );
    };

    return { renderedStages, currentStageColor, formatStageDate };
  },
};
</script>

<style scoped>
.journey {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f7f7f8;
  padding: 12px 16px 10px;
}

.journey__viewport {
  max-width: 740px;
  margin: 0 auto;
  overflow-x: auto;
}

.journey__rail {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.journey__stage {
  position: relative;
  min-width: 0;
  text-align: center;
}

.journey__line {
  position: absolute;
  top: 29px;
  left: 50%;
  width: 100%;
  height: 2px;
  background: #e5e7eb;
}

.journey__stage:last-child .journey__line {
  display: none;
}

.journey__stage--connector-reached .journey__line {
  background: var(--stage-color);
}

.journey__marker {
  position: relative;
  z-index: 1;
  display: flex;
  width: 22px;
  height: 22px;
  margin: 0 auto;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--stage-soft-color);
  border-radius: 50%;
  background: var(--stage-soft-color);
  color: var(--stage-color);
  font-size: 9px;
}

.journey__stage--reached .journey__marker {
  border-color: var(--stage-color);
  background: var(--stage-color);
  color: #fff;
}

.journey__stage--current .journey__marker {
  outline: 3px solid color-mix(in srgb, var(--stage-color) 22%, transparent);
}

.journey__label,
.journey__date {
  display: block;
}

.journey__label {
  height: 17px;
  color: #9ca3af;
  font-size: 11px;
  font-weight: 650;
}

.journey__stage--reached .journey__label {
  color: var(--stage-color);
}

.journey__date {
  min-height: 13px;
  margin-top: 4px;
  color: #9ca3af;
  font-size: 9px;
}

.journey__stage--reached .journey__date {
  color: color-mix(in srgb, var(--stage-color) 82%, #374151);
}

.journey__footer {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  max-width: 740px;
  align-items: center;
  gap: 16px;
  margin: 8px auto 0;
}

.journey__current {
  display: inline-flex;
  grid-column: 2;
  align-items: center;
  justify-self: center;
  gap: 7px;
  color: #374151;
  font-size: 10px;
  font-weight: 700;
}

.journey__current-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--current-stage-color);
}

.journey__toggle {
  display: inline-flex;
  grid-column: 3;
  min-height: 30px;
  align-items: center;
  justify-self: end;
  gap: 7px;
  border: 0;
  background: transparent;
  color: #08709e;
  font-size: 11px;
  font-weight: 700;
}

.journey__toggle:hover {
  color: #075a7e;
}

.journey__toggle svg {
  transition: transform 200ms ease;
}

.journey__toggle-icon--open {
  transform: rotate(180deg);
}

.journey__form {
  max-width: 740px;
  margin: 10px auto 0;
  border-top: 1px solid #e5e7eb;
  padding-top: 14px;
}

.journey-form-enter-active,
.journey-form-leave-active {
  max-height: 560px;
  overflow: hidden;
  transition: max-height 200ms ease, opacity 160ms ease, padding 200ms ease, margin 200ms ease;
}

.journey-form-enter-from,
.journey-form-leave-to {
  max-height: 0;
  margin-top: 0;
  padding-top: 0;
  opacity: 0;
}

@media (max-width: 640px) {
  .journey {
    padding: 12px 12px 10px;
  }

  .journey__rail {
    min-width: 500px;
  }

  .journey__footer {
    grid-template-columns: 1fr auto 1fr;
  }

  .journey__toggle {
    min-width: 34px;
    min-height: 34px;
    justify-content: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .journey__toggle svg,
  .journey-form-enter-active,
  .journey-form-leave-active {
    transition: none;
  }
}
</style>
